import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Phone,
    CheckCircle2,
    Loader2,
    ShieldCheck,
    Mail,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INSURANCE_CARRIERS = ["Blue Cross Blue Shield", "Optum"];
const RAY_EMAIL = "Raysmith@presentdaycounseling.com";

export default function Consultation() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const onChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const submitForm = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone) {
            toast.error("Please fill in your name, email, and phone.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/consultations`, form);
            setSubmitted(true);
            toast.success("Request received. You can call Ray right away.");
        } catch (err) {
            toast.error(
                "Something went wrong. Please try again or call directly.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="consultation-page">
            {/* HEADER */}
            <section className="pt-20 pb-12 md:pt-28 md:pb-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl pd-fade-up">
                        <p className="text-xs uppercase tracking-[0.35em] text-[#8C9C88] font-medium">
                            Consultation
                        </p>
                        <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight text-[#2C332A] break-words">
                            Book Your{" "}
                            <span className="italic text-[#8C9C88]">
                                Consultation
                            </span>
                        </h1>
                        <p className="mt-8 text-lg text-[#5B6358] leading-relaxed">
                            Two simple ways to start. Choose what feels right
                            — there's no wrong answer.
                        </p>
                        <div
                            className="mt-6 inline-flex flex-wrap items-center gap-x-5 gap-y-2 text-[#5B6358]"
                            data-testid="consultation-direct-contact"
                        >
                            <span className="text-sm uppercase tracking-[0.2em] text-[#8C9C88] font-medium">
                                Prefer to reach out directly?
                            </span>
                            <a
                                href={`mailto:${RAY_EMAIL}`}
                                className="inline-flex items-center gap-2 text-[#2C332A] font-medium border-b-2 border-[#8C9C88] pb-0.5 hover:text-[#8C9C88] transition-colors break-all"
                                data-testid="ray-email-link"
                            >
                                <Mail className="w-4 h-4 text-[#8C9C88] shrink-0" />
                                {RAY_EMAIL}
                            </a>
                            <a
                                href="tel:4052540805"
                                className="inline-flex items-center gap-2 text-[#2C332A] font-medium border-b-2 border-[#8C9C88] pb-0.5 hover:text-[#8C9C88] transition-colors"
                                data-testid="ray-phone-link"
                            >
                                <Phone className="w-4 h-4 text-[#8C9C88] shrink-0" />
                                405-254-0805
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* TWO OPTIONS */}
            <section className="pb-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-8">
                        {/* OPTION 1 — Free 15-minute */}
                        <div
                            className="rounded-2xl p-8 md:p-10 bg-[#F5F2EA] border border-[#E3DCC8]"
                            data-testid="option-free-card"
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-[11px] uppercase tracking-[0.2em] text-[#8C9C88] font-medium">
                                    Option 1
                                </span>
                                <span className="text-[11px] uppercase tracking-[0.2em] text-[#B5A38E] font-medium bg-white rounded-full px-3 py-1 border border-[#E3DCC8]">
                                    Free
                                </span>
                            </div>
                            <h2 className="mt-5 font-heading text-3xl text-[#2C332A] tracking-tight">
                                Free 15-Minute Consultation
                            </h2>

                            <div className="mt-6 flex items-baseline gap-2">
                                <span className="font-heading text-5xl text-[#2C332A]">
                                    Free
                                </span>
                                <span className="text-[#5B6358]">
                                    / 15 minutes
                                </span>
                            </div>

                            <p className="mt-6 text-[#5B6358] leading-relaxed">
                                A short, no-pressure call to say hi, hear
                                what's going on, and see if Ray is a good fit
                                for you. Fill out the short form and you'll
                                get her number right away to call.
                            </p>

                            {!submitted ? (
                                <form
                                    onSubmit={submitForm}
                                    className="mt-8 space-y-5"
                                    data-testid="free-consult-form"
                                >
                                    <div>
                                        <label
                                            htmlFor="name"
                                            className="block text-sm font-medium text-[#2C332A] mb-2"
                                        >
                                            Name
                                        </label>
                                        <input
                                            id="name"
                                            name="name"
                                            value={form.name}
                                            onChange={onChange}
                                            required
                                            className="w-full rounded-lg border border-[#E3DCC8] bg-white px-4 py-3 text-[#2C332A] placeholder:text-[#9AA294] focus:outline-none focus:ring-2 focus:ring-[#8C9C88] focus:border-transparent transition"
                                            placeholder="Your full name"
                                            data-testid="input-name"
                                        />
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="email"
                                            className="block text-sm font-medium text-[#2C332A] mb-2"
                                        >
                                            Email
                                        </label>
                                        <input
                                            id="email"
                                            name="email"
                                            type="email"
                                            value={form.email}
                                            onChange={onChange}
                                            required
                                            className="w-full rounded-lg border border-[#E3DCC8] bg-white px-4 py-3 text-[#2C332A] placeholder:text-[#9AA294] focus:outline-none focus:ring-2 focus:ring-[#8C9C88] focus:border-transparent transition"
                                            placeholder="you@example.com"
                                            data-testid="input-email"
                                        />
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="phone"
                                            className="block text-sm font-medium text-[#2C332A] mb-2"
                                        >
                                            Phone
                                        </label>
                                        <input
                                            id="phone"
                                            name="phone"
                                            type="tel"
                                            value={form.phone}
                                            onChange={onChange}
                                            required
                                            className="w-full rounded-lg border border-[#E3DCC8] bg-white px-4 py-3 text-[#2C332A] placeholder:text-[#9AA294] focus:outline-none focus:ring-2 focus:ring-[#8C9C88] focus:border-transparent transition"
                                            placeholder="(555) 123-4567"
                                            data-testid="input-phone"
                                        />
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="message"
                                            className="block text-sm font-medium text-[#2C332A] mb-2"
                                        >
                                            Brief message
                                            <span className="text-[#9AA294] font-normal">
                                                {" "}
                                                (optional)
                                            </span>
                                        </label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            value={form.message}
                                            onChange={onChange}
                                            rows="4"
                                            className="w-full rounded-lg border border-[#E3DCC8] bg-white px-4 py-3 text-[#2C332A] placeholder:text-[#9AA294] focus:outline-none focus:ring-2 focus:ring-[#8C9C88] focus:border-transparent transition resize-none"
                                            placeholder="A line or two about what you'd like to talk about."
                                            data-testid="input-message"
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#8C9C88] text-white px-6 py-3.5 font-medium shadow-sm transition-all duration-300 hover:bg-[#7A8A76] hover:-translate-y-0.5 hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
                                        data-testid="submit-consultation"
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Submitting…
                                            </>
                                        ) : (
                                            "Submit & Book 15-Minute Consultation"
                                        )}
                                    </button>
                                </form>
                            ) : (
                                <div
                                    className="mt-8 rounded-xl bg-white border border-[#E3DCC8] p-6 pd-fade-up"
                                    data-testid="free-consult-success"
                                >
                                    <CheckCircle2 className="w-10 h-10 text-[#8C9C88]" />
                                    <h3 className="mt-4 font-heading text-2xl text-[#2C332A]">
                                        Thank you — your request is in.
                                    </h3>
                                    <p className="mt-3 text-[#5B6358] leading-relaxed">
                                        Ray will see your message. For an
                                        immediate 15-minute consultation, go
                                        ahead and call right now:
                                    </p>
                                    <a
                                        href="tel:4052540805"
                                        className="mt-6 inline-flex items-center justify-center gap-3 w-full rounded-full bg-[#8C9C88] text-white px-6 py-4 text-lg font-medium shadow-sm transition-all duration-300 hover:bg-[#7A8A76] hover:-translate-y-0.5 hover:shadow-md"
                                        data-testid="success-call-button"
                                    >
                                        <Phone className="w-5 h-5" />
                                        Call Ray: 405-254-0805
                                    </a>
                                    <p className="mt-4 text-sm text-[#5B6358]">
                                        Prefer to wait? Ray will also reach
                                        out using the contact details you
                                        provided.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* OPTION 2 — $150 First Session */}
                        <div
                            className="rounded-2xl p-8 md:p-10 bg-white border border-[#E3DCC8] relative overflow-hidden"
                            data-testid="option-paid-card"
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-[11px] uppercase tracking-[0.2em] text-[#8C9C88] font-medium">
                                    Option 2
                                </span>
                                <span className="text-[11px] uppercase tracking-[0.2em] text-white bg-[#8C9C88] rounded-full px-3 py-1">
                                    $150 · 50 min
                                </span>
                            </div>
                            <h2 className="mt-5 font-heading text-3xl text-[#2C332A] tracking-tight">
                                50-Minute First Session
                            </h2>

                            <div className="mt-6 flex items-baseline gap-2">
                                <span className="font-heading text-5xl text-[#2C332A]">
                                    $150
                                </span>
                                <span className="text-[#5B6358]">
                                    / 50 minutes
                                </span>
                            </div>

                            <p className="mt-6 text-[#5B6358] leading-relaxed">
                                Your first full session with Ray. We'll talk
                                through what brought you in, what you're
                                hoping to work on, and how we'd move forward
                                together.
                            </p>

                            <ul className="mt-6 space-y-2 text-[#5B6358]">
                                <li className="flex items-start gap-2">
                                    <CheckCircle2 className="w-4 h-4 text-[#8C9C88] mt-1 shrink-0" />
                                    One-on-one with Ray
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle2 className="w-4 h-4 text-[#8C9C88] mt-1 shrink-0" />
                                    Private, confidential, judgment-free
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle2 className="w-4 h-4 text-[#8C9C88] mt-1 shrink-0" />
                                    Deeper, focused support
                                </li>
                            </ul>

                            <div className="mt-8 rounded-xl bg-[#F5F2EA] border border-[#E3DCC8] p-6">
                                <p className="text-[#2C332A] font-medium">
                                    Ready to book the 50-minute session?
                                </p>
                                <p className="mt-2 text-[#5B6358] leading-relaxed">
                                    Give Ray a call and she'll get you
                                    scheduled directly.
                                </p>
                                <a
                                    href="tel:4052540805"
                                    className="mt-5 inline-flex items-center justify-center gap-2 w-full rounded-full bg-[#8C9C88] text-white px-6 py-3.5 font-medium shadow-sm transition-all duration-300 hover:bg-[#7A8A76] hover:-translate-y-0.5 hover:shadow-md"
                                    data-testid="paid-call-button"
                                >
                                    <Phone className="w-4 h-4" />
                                    Call Ray: 405-254-0805
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* PRICING & INSURANCE */}
            <section
                className="pb-24"
                data-testid="consultation-insurance-section"
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="rounded-2xl p-8 md:p-10 bg-[#F5F2EA] border border-[#E3DCC8]">
                        <div className="grid md:grid-cols-2 gap-10">
                            <div>
                                <p className="text-xs uppercase tracking-[0.35em] text-[#8C9C88] font-medium">
                                    Pricing
                                </p>
                                <h2 className="mt-3 font-heading text-2xl sm:text-3xl text-[#2C332A] tracking-tight">
                                    Determined by your coverage.
                                </h2>
                                <p className="mt-5 text-[#5B6358] leading-relaxed">
                                    Session fees are primarily determined by
                                    your insurance carrier and your specific
                                    plan benefits. For clients paying out of
                                    pocket, Ray's standard rate is{" "}
                                    <span className="text-[#2C332A] font-medium">
                                        $150 per 50-minute session.
                                    </span>
                                </p>
                                <p className="mt-4 text-[#5B6358] leading-relaxed">
                                    Unsure what applies to you? A free
                                    15-minute consultation is the easiest way
                                    to find out.
                                </p>
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-[0.35em] text-[#8C9C88] font-medium">
                                    Insurance accepted
                                </p>
                                <h2 className="mt-3 font-heading text-2xl sm:text-3xl text-[#2C332A] tracking-tight">
                                    In-network with:
                                </h2>
                                <ul
                                    className="mt-5 space-y-3"
                                    data-testid="consultation-insurance-list"
                                >
                                    {INSURANCE_CARRIERS.map((name) => (
                                        <li
                                            key={name}
                                            className="flex items-center gap-3 rounded-xl bg-white border border-[#E3DCC8] px-4 py-3 text-[#2C332A] font-medium"
                                        >
                                            <ShieldCheck className="w-5 h-5 text-[#8C9C88]" />
                                            {name}
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-4 text-sm text-[#5B6358] leading-relaxed">
                                    Coverage and copay vary by plan. Ray's
                                    office can help verify your benefits
                                    before your first session.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
